import { motion } from 'framer-motion';
import { Activity, Apple, BrainCircuit, HeartHandshake, Zap, Moon } from 'lucide-react';

const features = [
  {
    icon: <Activity className="w-6 h-6" />,
    title: "Dynamic Workouts",
    description: "Customized plans evaluating your performance every 7 days. Features Omnio Pose for visual feedback and form correction."
  },
  {
    icon: <Apple className="w-6 h-6" />,
    title: "Intelligent Meals & Fridge",
    description: "Generates nutrition plans based on your inventory. Subtracts used ingredients, tracks expiration, and creates dynamic shopping lists."
  },
  {
    icon: <BrainCircuit className="w-6 h-6" />,
    title: "Claudia: Mental Health Coach",
    description: "Tracks emotional patterns, workload stress, and consistency to reflect back behaviors for true internal change."
  },
  {
    icon: <HeartHandshake className="w-6 h-6" />,
    title: "Mindfulness",
    description: "Your peace room. Daily gratitude and mindful breathing exercises to help you connect with your inner self."
  },
  {
    icon: <Moon className="w-6 h-6" />,
    title: "Wellness Hub",
    description: "Tracks sleep, water, and supplements. Adjusts your workouts and recovery periods dynamically based on your logs."
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Mirror Me & Outcomes",
    description: "A raw, honest reflection based purely on your actions, helping you see the results of your choices before you make them."
  }
];

export default function Features() {
  return (
    <section id="features" className="py-24 bg-omnio-black relative overflow-hidden">
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-omnio-charcoal/20 blur-[150px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-omnio-white tracking-tight mb-6"
          >
            A Holistic Approach to You
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-omnio-silver font-light"
          >
            Omnio is not limited to fitness. Every part of the app communicates with every other part, learning your patterns to build discipline and long-term self-improvement.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-8 rounded-2xl bg-omnio-dark border border-omnio-charcoal hover:border-omnio-gray transition-colors group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-omnio-charcoal/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-12 h-12 bg-omnio-charcoal rounded-xl flex items-center justify-center text-omnio-white mb-6 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-omnio-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-omnio-gray font-light leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
